/* eslint-disable max-lines-per-function */
import { ReactElement, useMemo } from 'react';

import {
   PatientMedicine,
   PrescriptionHistoryPrescription,
   PrescriptionHistoryQuery,
} from '@dreryk/egabinet-prescription-frontend-api';

import {
   colorsMatrix,
   JustifyContent,
   Padding,
   RowDetailsCell,
   Text,
   UncontrolledCheckbox,
} from '@dreryk/react-components';
import { dateFormatter, Translate } from '@dreryk/react-common';

import {
   ConfidentialAccessPendingButton,
   ConfidentialAccessRequestButton,
   ConfidentialMask,
} from '~libs.desktop.shared.medical-data-confidentiality';
import { MedicineFullNameReadOnlyCell, MedicineSafetyIndicators } from '~libs.desktop.shared.medicine';
import { formatMedicinePackage } from '~libs.shared.medicine';
import { protectFeature } from '~libs.shared.system';

import {
   PrescriptionHistoryRenderOptionsCell,
   PrescriptionHistorySelection,
} from '../PatientPrescriptionHistoryTable.types';
import { generateColumnsWidths } from '../PatientPrescriptionHistoryTable.hooks';
import { isSelectablePrescription } from '../PatientPrescriptionHistoryTable.utils';
import { translationKeys } from '../../translations';

import { PrescriptionHistoryDosageCell } from './PrescriptionHistoryDosageCell';
import { StyledConfidentialText, StyledPrescriptionHistoryRowDetails } from './PrescriptionHistoryRowDetails.styled';

type PrescriptionHistoryRowDetailsProps = {
   readonly prescription: PrescriptionHistoryPrescription;
   readonly renderOptionsCell?: PrescriptionHistoryRenderOptionsCell;
   readonly selection?: PrescriptionHistorySelection;
};

const renderAccessButton = (prescription: PrescriptionHistoryPrescription): ReactElement | null => {
   if (prescription.pendingAccessRequest) {
      return <ConfidentialAccessPendingButton />;
   }
   if (prescription.medicalDocumentId === undefined) {
      return null;
   }
   return (
      <ConfidentialAccessRequestButton
         resourceId={prescription.medicalDocumentId}
         resourceType={'MEDICAL_DOCUMENT'}
         refetchQuery={PrescriptionHistoryQuery}
      />
   );
};

const renderMedicineName = (prescription: PrescriptionHistoryPrescription): ReactElement | null => {
   if (!prescription.medicine) {
      return null;
   }
   return (
      <MedicineFullNameReadOnlyCell
         medicine={prescription.medicine}
         reimbursementCode={prescription.reimbursementCode}
      />
   );
};

const renderOptions = (
   prescription: PrescriptionHistoryPrescription,
   patientMedicine: PatientMedicine | undefined,
   renderOptionsCell: PrescriptionHistoryRenderOptionsCell | undefined
): ReactElement | null => {
   if (!patientMedicine) {
      return null;
   }
   return renderOptionsCell?.(prescription, patientMedicine) ?? null;
};

export const PrescriptionHistoryRowDetails = (props: PrescriptionHistoryRowDetailsProps): JSX.Element => {
   const medicineDatabaseExtendedAvailable = protectFeature('extendedMedicineDatabase');
   const columnsWidth = generateColumnsWidths(medicineDatabaseExtendedAvailable, Boolean(props.selection));
   const hidden = props.prescription.confidential === true && props.prescription.hasAccess !== true;
   const medicine = hidden ? undefined : props.prescription.medicine;
   const patientMedicine: PatientMedicine | undefined = useMemo(
      () =>
         medicine
            ? {
                 medicine: medicine,
                 reimbursements: [],
                 patientContinuouslyUsedMedicineId: props.prescription.patientContinuouslyUsedMedicineId,
              }
            : undefined,
      [medicine, props.prescription.patientContinuouslyUsedMedicineId]
   );

   if (hidden) {
      return (
         <StyledPrescriptionHistoryRowDetails testId={`prescription-${props.prescription.prescriptionId}`}>
            {props.selection ? (
               <RowDetailsCell
                  testId={'prescriptionCheckbox'}
                  width={columnsWidth.checkbox}
                  padding={Padding.LEFT_ONLY}
               >
                  {null}
               </RowDetailsCell>
            ) : null}
            <RowDetailsCell testId={'medicineName'} width={columnsWidth.medicineName}>
               <ConfidentialMask entry={{ confidential: true, hasAccess: false }}>{() => null}</ConfidentialMask>
               <StyledConfidentialText testId={'prescriptionConfidential'} color={colorsMatrix.grey.dark}>
                  <Translate>{translationKeys.prescriptionConfidential}</Translate>
               </StyledConfidentialText>
            </RowDetailsCell>
            <RowDetailsCell testId={'dosage'} width={columnsWidth.dosage}>
               <ConfidentialMask entry={{ confidential: true, hasAccess: false }}>{() => null}</ConfidentialMask>
            </RowDetailsCell>
            <RowDetailsCell testId={'package'} width={columnsWidth.package}>
               <ConfidentialMask entry={{ confidential: true, hasAccess: false }}>{() => null}</ConfidentialMask>
            </RowDetailsCell>
            <RowDetailsCell
               testId={'options'}
               width={`calc(${columnsWidth.quantity} + ${columnsWidth.issuedFrom} + ${columnsWidth.expander})`}
               padding={Padding.NONE}
               justifyContent={JustifyContent.END}
            >
               {renderAccessButton(props.prescription)}
            </RowDetailsCell>
         </StyledPrescriptionHistoryRowDetails>
      );
   }

   return (
      <StyledPrescriptionHistoryRowDetails testId={`prescription-${props.prescription.prescriptionId}`}>
         {props.selection ? (
            <RowDetailsCell testId={'prescriptionCheckbox'} width={columnsWidth.checkbox} padding={Padding.LEFT_ONLY}>
               {isSelectablePrescription(props.prescription) ? (
                  <UncontrolledCheckbox
                     testId={`selectPrescription-${props.prescription.prescriptionId}`}
                     selected={props.selection.isPrescriptionSelected(props.prescription.prescriptionId)}
                     onSelectionChange={() => props.selection!.togglePrescription(props.prescription.prescriptionId)}
                     id={`selectPrescription-${props.prescription.prescriptionId}`}
                     name={`selectPrescription-${props.prescription.prescriptionId}`}
                  />
               ) : null}
            </RowDetailsCell>
         ) : null}
         <RowDetailsCell testId={'medicineName'} width={columnsWidth.medicineName}>
            {renderMedicineName(props.prescription)}
         </RowDetailsCell>
         {medicineDatabaseExtendedAvailable ? (
            <RowDetailsCell testId={'safetyIndicators'} width={columnsWidth.safetyIndicators}>
               {medicine ? <MedicineSafetyIndicators medicineId={medicine.id} /> : null}
            </RowDetailsCell>
         ) : null}
         <RowDetailsCell testId={'dosage'} width={columnsWidth.dosage}>
            <PrescriptionHistoryDosageCell prescription={props.prescription} />
         </RowDetailsCell>
         <RowDetailsCell testId={'package'} width={columnsWidth.package}>
            <Text testId={'package'}>{medicine ? formatMedicinePackage(medicine) : null}</Text>
         </RowDetailsCell>
         <RowDetailsCell testId={'quantity'} width={columnsWidth.quantity}>
            <Text testId={'quantity'}>{props.prescription.quantity ?? null}</Text>
         </RowDetailsCell>
         <RowDetailsCell
            testId={'issuedFrom'}
            width={columnsWidth.issuedFrom}
            justifyContent={JustifyContent.CENTER}
            padding={Padding.NONE}
         >
            <Text testId={'issuedFrom'}>
               {props.prescription.issuedFrom ? dateFormatter(props.prescription.issuedFrom) : null}
            </Text>
         </RowDetailsCell>
         <RowDetailsCell
            testId={'options'}
            width={columnsWidth.expander}
            padding={Padding.NONE}
            justifyContent={JustifyContent.END}
         >
            {renderOptions(props.prescription, patientMedicine, props.renderOptionsCell)}
         </RowDetailsCell>
      </StyledPrescriptionHistoryRowDetails>
   );
};
